// The sync engine: the single piece of code that knows about both the
// Zustand synced store and the SyncClient. Per plan/02 §SDK abstraction
// layer + plan/02 §Subscription lifecycle. See tasks/43 §4-5 for the
// v3 profile_routines junction.
//
// Responsibilities:
//
//   1. On `setTree(next)`: diff vs. previous tree, enqueue an upsert per
//      changed record and a delete per removed record.
//   2. On `useRoutineProgress` flush: enqueue the (profile, routine, day)
//      progress upsert.
//   3. On a remote subscribe event: run the relevant merge function and
//      `setTree(...)` / `saveDailyProgress(...)`. The "applying remote"
//      flag stops these writes from looping back into the outbox.
//   4. On reconnect / boot-with-auth: getFullList catch-up + outbox
//      drain.
//
// Subscription filters per plan/02 §Subscription lifecycle:
//
//   * profiles/routines/tasks/profile_routines: `family = "<id>"`
//   * progress: `family = "<id>" && date_key = "<key>"` (re-subscribe on
//     day change; covers ALL profiles for today)

import { dateKey as todayDateKey } from '../../lib/dateKey';
import { bySortIndex } from '../../lib/sortIndex';
import { loadDailyProgress, saveDailyProgress } from '../../lib/storage/indexedDb';
import type { DailyProgress, Id, Profile, Routine, SyncedTree, Task } from '../../lib/types';
import { useDeviceStore } from '../../store/device';
import { setTreeChangeListener, useSyncedStore } from '../../store/synced';
import { emitProgress } from '../progress/progressEvents';
import { getSyncClient, type SyncClient } from './client';
import { mergeProgress, mergeTreeRecord } from './conflictMerge';
import {
  applyRemoteToProgress,
  profileRoutineToRow,
  profileToRow,
  progressToRemote,
  routineToRow,
  taskToRow,
  type ProfileRoutineRow,
  type ProfileRow,
  type ProgressRow,
  type RoutineRow,
  type TaskRow,
} from './mapping';
import { createOutbox, type Outbox } from './outbox';
import {
  SyncAuthError,
  type CollectionName,
  type RemoteRecord,
  type SubscribeEvent,
} from './types';

export type EngineStatus = 'stopped' | 'starting' | 'running' | 'auth-failed';

export interface EngineStartOptions {
  syncCode: string;
}

export type RecoverResult = { kind: 'recovered' } | { kind: 'not-running' };

export interface SyncEngine {
  status(): EngineStatus;
  familyId(): string | null;
  start(opts: EngineStartOptions): Promise<void>;
  stop(): Promise<void>;
  notifyTreeChange(prev: SyncedTree | null, next: SyncedTree): Promise<void>;
  notifyProgress(daily: DailyProgress, routineId: Id): Promise<void>;
  onActiveProfileChanged(profileId: Id | null): void;
  onDayChanged(key: string): void;
  recover(): Promise<RecoverResult>;
  __resetForTests(): Promise<void>;
  __awaitIdle(): Promise<void>;
}

// ----- diff helper -----

interface FlatTree {
  profiles: Map<string, ProfileRow>;
  routines: Map<string, RoutineRow>;
  tasks: Map<string, TaskRow>;
  profileRoutines: Map<string, ProfileRoutineRow>;
}

function flatten(tree: SyncedTree | null, familyId: string): FlatTree {
  const profiles = new Map<string, ProfileRow>();
  const routines = new Map<string, RoutineRow>();
  const tasks = new Map<string, TaskRow>();
  const profileRoutines = new Map<string, ProfileRoutineRow>();
  if (!tree) return { profiles, routines, tasks, profileRoutines };
  for (const profile of tree.profiles) {
    profiles.set(profile.id, profileToRow(profile, familyId));
    profile.routineIds.forEach((routineId, i) => {
      const row = profileRoutineToRow(profile.id, routineId, i, familyId, profile.updatedAt);
      profileRoutines.set(row.id, row);
    });
  }
  for (const routine of tree.routines) {
    routines.set(routine.id, routineToRow(routine, familyId));
    for (const task of routine.tasks) {
      tasks.set(task.id, taskToRow(task, routine.id, familyId));
    }
  }
  return { profiles, routines, tasks, profileRoutines };
}

interface DiffOps {
  upserts: Array<
    | { collection: 'profiles'; record: ProfileRow }
    | { collection: 'routines'; record: RoutineRow }
    | { collection: 'tasks'; record: TaskRow }
    | { collection: 'profile_routines'; record: ProfileRoutineRow }
  >;
  deletes: Array<{ collection: CollectionName; id: string }>;
}

function diffMaps<R extends RemoteRecord>(
  prev: Map<string, R>,
  next: Map<string, R>,
): { upserts: R[]; deletes: string[] } {
  const upserts: R[] = [];
  const deletes: string[] = [];
  for (const [id, row] of next) {
    const before = prev.get(id);
    if (!before || before.updated !== row.updated) upserts.push(row);
  }
  for (const id of prev.keys()) {
    if (!next.has(id)) deletes.push(id);
  }
  return { upserts, deletes };
}

export function diffTree(
  prev: SyncedTree | null,
  next: SyncedTree | null,
  familyId: string,
): DiffOps {
  const prevFlat = flatten(prev, familyId);
  const nextFlat = flatten(next, familyId);

  const profileDiff = diffMaps(prevFlat.profiles, nextFlat.profiles);
  const routineDiff = diffMaps(prevFlat.routines, nextFlat.routines);
  const taskDiff = diffMaps(prevFlat.tasks, nextFlat.tasks);
  const junctionDiff = diffMaps(prevFlat.profileRoutines, nextFlat.profileRoutines);

  const ops: DiffOps = { upserts: [], deletes: [] };
  for (const r of profileDiff.upserts) ops.upserts.push({ collection: 'profiles', record: r });
  for (const r of routineDiff.upserts) ops.upserts.push({ collection: 'routines', record: r });
  for (const r of taskDiff.upserts) ops.upserts.push({ collection: 'tasks', record: r });
  for (const r of junctionDiff.upserts) {
    ops.upserts.push({ collection: 'profile_routines', record: r });
  }
  for (const id of profileDiff.deletes) ops.deletes.push({ collection: 'profiles', id });
  for (const id of routineDiff.deletes) ops.deletes.push({ collection: 'routines', id });
  for (const id of taskDiff.deletes) ops.deletes.push({ collection: 'tasks', id });
  for (const id of junctionDiff.deletes) ops.deletes.push({ collection: 'profile_routines', id });
  return ops;
}

// ----- engine internals -----

function progressFilter(familyId: string, dateKey: string): string {
  return `family = "${familyId}" && date_key = "${dateKey}"`;
}

function applyRemoteRecordToTree(
  tree: SyncedTree,
  collection: CollectionName,
  record: RemoteRecord,
): SyncedTree {
  if (collection === 'profiles') {
    const row = record as ProfileRow;
    const local = tree.profiles.find((p) => p.id === row.id) ?? null;
    const remoteAsLocal: Profile = {
      id: row.id,
      name: row.name,
      avatarEmoji: row.avatar_emoji,
      theme: row.theme,
      color: row.accent_color,
      sortIndex: row.sort_index,
      createdAt: local?.createdAt ?? row.updated,
      updatedAt: row.updated,
      // routineIds stay local — profile_routines rows carry that state.
      routineIds: local?.routineIds ?? [],
    };
    const merged = mergeTreeRecord(local ? { id: local.id, updatedAt: local.updatedAt } : null, {
      id: row.id,
      updatedAt: row.updated,
    });
    const winner = merged.updatedAt === row.updated ? remoteAsLocal : local!;
    if (!local) {
      return { ...tree, profiles: [...tree.profiles, remoteAsLocal].sort(bySortIndex) };
    }
    return {
      ...tree,
      profiles: tree.profiles.map((p) => (p.id === row.id ? winner : p)),
    };
  }

  if (collection === 'routines') {
    const row = record as RoutineRow;
    // v3: routines live in the library, keyed only by id — no profile owner.
    const local = tree.routines.find((r) => r.id === row.id) ?? null;
    const remoteAsLocal: Routine = {
      id: row.id,
      name: row.name,
      emoji: row.emoji,
      sortIndex: row.sort_index,
      createdAt: local?.createdAt ?? row.updated,
      updatedAt: row.updated,
      tasks: local?.tasks ?? [],
    };
    const merged = mergeTreeRecord(local ? { id: local.id, updatedAt: local.updatedAt } : null, {
      id: row.id,
      updatedAt: row.updated,
    });
    const winner = merged.updatedAt === row.updated ? remoteAsLocal : local!;
    if (!local) {
      return { ...tree, routines: [...tree.routines, remoteAsLocal].sort(bySortIndex) };
    }
    return {
      ...tree,
      routines: tree.routines.map((r) => (r.id === row.id ? winner : r)),
    };
  }

  if (collection === 'profile_routines') {
    const row = record as ProfileRoutineRow;
    // Assignment change: rebuild the profile's routineIds by dropping the
    // routine (if present) and inserting it at the row's sort_index.
    return {
      ...tree,
      profiles: tree.profiles.map((p) => {
        if (p.id !== row.profile) return p;
        const withoutRoutine = p.routineIds.filter((id) => id !== row.routine);
        const clamped = Math.max(0, Math.min(row.sort_index, withoutRoutine.length));
        const nextIds = [
          ...withoutRoutine.slice(0, clamped),
          row.routine,
          ...withoutRoutine.slice(clamped),
        ];
        // Reference-equal no-op when nothing actually changed.
        if (
          nextIds.length === p.routineIds.length &&
          nextIds.every((id, i) => id === p.routineIds[i])
        ) {
          return p;
        }
        return { ...p, routineIds: nextIds, updatedAt: row.updated };
      }),
    };
  }

  // collection === 'tasks'
  const row = record as TaskRow;
  const routineExists = tree.routines.some((r) => r.id === row.routine);
  if (!routineExists) return tree; // Out-of-order delivery — caller buffers.
  const local =
    tree.routines.find((r) => r.id === row.routine)?.tasks.find((t) => t.id === row.id) ?? null;
  const remoteAsLocal: Task = {
    id: row.id,
    name: row.name,
    emoji: row.emoji,
    tag: row.tag,
    sortIndex: row.sort_index,
    createdAt: local?.createdAt ?? row.updated,
    updatedAt: row.updated,
  };
  const merged = mergeTreeRecord(local ? { id: local.id, updatedAt: local.updatedAt } : null, {
    id: row.id,
    updatedAt: row.updated,
  });
  const winner = merged.updatedAt === row.updated ? remoteAsLocal : local!;
  return {
    ...tree,
    routines: tree.routines.map((r) => {
      if (r.id !== row.routine) return r;
      if (!local) {
        return { ...r, tasks: [...r.tasks, remoteAsLocal].sort(bySortIndex) };
      }
      return {
        ...r,
        tasks: r.tasks.map((t) => (t.id === row.id ? winner : t)),
      };
    }),
  };
}

function deleteRemoteRecordFromTree(
  tree: SyncedTree,
  collection: CollectionName,
  id: string,
): SyncedTree {
  if (collection === 'profiles') {
    return { ...tree, profiles: tree.profiles.filter((p) => p.id !== id) };
  }
  if (collection === 'routines') {
    // v3: remove from the library and unlink from every profile's routineIds.
    const routines = tree.routines.filter((r) => r.id !== id);
    const profiles = tree.profiles.map((p) =>
      p.routineIds.includes(id)
        ? { ...p, routineIds: p.routineIds.filter((rid) => rid !== id) }
        : p,
    );
    return { ...tree, routines, profiles };
  }
  if (collection === 'tasks') {
    return {
      ...tree,
      routines: tree.routines.map((r) => ({ ...r, tasks: r.tasks.filter((t) => t.id !== id) })),
    };
  }
  if (collection === 'profile_routines') {
    // Junction id encodes (profile, routine); we don't have the raw pair
    // when the event only carries the id, so walk the profiles and try to
    // strip a matching row. In practice the event payload always contains
    // the record on `create`/`update`; on delete PB gives us only the id.
    // We recover the routineId by matching against every profile's
    // routineIds and checking if the deterministic junction id matches.
    // Cheaper: keep a small local index. Since delete events are rare and
    // this is a defensive path, the linear scan is fine.
    return {
      ...tree,
      profiles: tree.profiles.map((p) => {
        for (const rid of p.routineIds) {
          if (deterministicJunctionId(p.id, rid) === id) {
            return { ...p, routineIds: p.routineIds.filter((x) => x !== rid) };
          }
        }
        return p;
      }),
    };
  }
  return tree;
}

// Local copy of profileRoutineRowId so the engine doesn't cyclically import.
function deterministicJunctionId(profileId: string, routineId: string): string {
  const input = `${profileId}:${routineId}`;
  let h = 0xcbf29ce484222325n;
  for (let i = 0; i < input.length; i++) {
    h ^= BigInt(input.codePointAt(i)!);
    h = BigInt.asUintN(64, h * 0x100000001b3n);
  }
  return h.toString(36).padStart(15, '0').slice(0, 15);
}

// ----- Engine factory -----

export function createSyncEngine(): SyncEngine {
  let status: EngineStatus = 'stopped';
  let client: SyncClient | null = null;
  let familyId: string | null = null;
  let outbox: Outbox | null = null;

  let applyingRemote = false;

  const treeUnsubs: Array<() => void> = [];
  let progressUnsub: (() => void) | null = null;
  let reconnectUnsub: (() => void) | null = null;

  const pending = new Set<Promise<unknown>>();
  function track<T>(p: Promise<T>): Promise<T> {
    pending.add(p);
    void p
      .catch(() => undefined)
      .finally(() => {
        pending.delete(p);
      });
    return p;
  }

  // Same buffering pattern as pendingTasksByRoutine — the four tree
  // collections subscribe independently so cross-collection ordering
  // isn't guaranteed. Task-before-routine and profile_routines-before-
  // routine are the two race paths we need to buffer.
  const PENDING_PER_ROUTINE_CAP = 256;
  const pendingTasksByRoutine = new Map<string, TaskRow[]>();
  const pendingJunctionByRoutine = new Map<string, ProfileRoutineRow[]>();
  const pendingOverflowWarned = new Set<string>();

  function bufferPendingTask(row: TaskRow): void {
    bufferInto(pendingTasksByRoutine, row.routine, row);
  }

  function bufferPendingJunction(row: ProfileRoutineRow): void {
    bufferInto(pendingJunctionByRoutine, row.routine, row);
  }

  function bufferInto<R extends { id: string; routine: string }>(
    buf: Map<string, R[]>,
    key: string,
    row: R,
  ): void {
    const existing = buf.get(key);
    if (!existing) {
      buf.set(key, [row]);
      return;
    }
    const existingIdx = existing.findIndex((r) => r.id === row.id);
    if (existingIdx !== -1) {
      // Latest event wins for the same id.
      existing.splice(existingIdx, 1, row);
      return;
    }
    if (existing.length >= PENDING_PER_ROUTINE_CAP) {
      existing.shift();
      if (!pendingOverflowWarned.has(key)) {
        pendingOverflowWarned.add(key);
        console.warn('[sync:engine] buffer cap reached, dropping oldest', {
          routine: key,
          cap: PENDING_PER_ROUTINE_CAP,
        });
      }
    }
    existing.push(row);
  }

  function drainPendingForRoutine(tree: SyncedTree, routineId: string): SyncedTree {
    let next = tree;
    const tasks = pendingTasksByRoutine.get(routineId);
    if (tasks && tasks.length > 0) {
      pendingTasksByRoutine.delete(routineId);
      for (const row of tasks) next = applyRemoteRecordToTree(next, 'tasks', row);
    }
    const junctions = pendingJunctionByRoutine.get(routineId);
    if (junctions && junctions.length > 0) {
      pendingJunctionByRoutine.delete(routineId);
      for (const row of junctions) next = applyRemoteRecordToTree(next, 'profile_routines', row);
    }
    pendingOverflowWarned.delete(routineId);
    return next;
  }

  function drainAllPending(tree: SyncedTree): SyncedTree {
    if (pendingTasksByRoutine.size === 0 && pendingJunctionByRoutine.size === 0) return tree;
    let next = tree;
    const keys = new Set<string>([
      ...pendingTasksByRoutine.keys(),
      ...pendingJunctionByRoutine.keys(),
    ]);
    for (const routineId of keys) next = drainPendingForRoutine(next, routineId);
    return next;
  }

  async function stopInternal(): Promise<void> {
    setTreeChangeListener(null);
    for (const u of treeUnsubs.splice(0)) u();
    if (progressUnsub) progressUnsub();
    progressUnsub = null;
    if (reconnectUnsub) reconnectUnsub();
    reconnectUnsub = null;
    if (client) client.authClear();
    client = null;
    familyId = null;
    outbox = null;
    pendingTasksByRoutine.clear();
    pendingJunctionByRoutine.clear();
    pendingOverflowWarned.clear();
    status = 'stopped';
  }

  function applyRemote<T>(fn: () => T): T {
    applyingRemote = true;
    try {
      return fn();
    } finally {
      applyingRemote = false;
    }
  }

  function computeNextTree(
    tree: SyncedTree,
    collection: CollectionName,
    event: SubscribeEvent,
  ): SyncedTree | null {
    if (event.action === 'delete') {
      return deleteRemoteRecordFromTree(tree, collection, event.record.id);
    }
    if (collection === 'tasks') {
      const row = event.record as TaskRow;
      if (!tree.routines.some((r) => r.id === row.routine)) {
        bufferPendingTask(row);
        return null;
      }
      return applyRemoteRecordToTree(tree, 'tasks', row);
    }
    if (collection === 'profile_routines') {
      const row = event.record as ProfileRoutineRow;
      if (!tree.routines.some((r) => r.id === row.routine)) {
        bufferPendingJunction(row);
        return null;
      }
      return applyRemoteRecordToTree(tree, 'profile_routines', row);
    }
    if (collection === 'routines') {
      const row = event.record as RoutineRow;
      const afterRoutine = applyRemoteRecordToTree(tree, 'routines', row);
      return drainPendingForRoutine(afterRoutine, row.id);
    }
    return applyRemoteRecordToTree(tree, collection, event.record);
  }

  async function handleTreeEvent(collection: CollectionName, event: SubscribeEvent): Promise<void> {
    if (applyingRemote) return;
    const state = useSyncedStore.getState();
    const tree = state.tree;
    if (!tree) return;
    const next = computeNextTree(tree, collection, event);
    if (next === null || next === tree) return;
    applyRemote(() => state.setTree(next));
  }

  async function handleProgressEvent(event: SubscribeEvent): Promise<void> {
    if (applyingRemote) return;
    if (event.action === 'delete') return;
    const row = event.record as ProgressRow;
    const local = await loadDailyProgress(row.profile, row.date_key);
    const remoteDaily = applyRemoteToProgress([row]);
    if (!remoteDaily) return;
    const merged = mergeProgress(local, remoteDaily);
    await saveDailyProgress(merged);
    emitProgress(merged);

    const remoteArr: boolean[] = row.completed ?? [];
    const mergedArr: boolean[] = merged.routines[row.routine] ?? [];
    const richer =
      mergedArr.length > remoteArr.length ||
      mergedArr.some((v, i) => v && !(remoteArr[i] ?? false));
    if (richer && status === 'running') {
      const cl = client;
      const fid = familyId;
      const ob = outbox;
      if (cl && fid && ob) {
        const reuploadRow = progressToRemote(merged, fid, row.routine);
        void track(
          (async () => {
            await ob.enqueue({
              id: `progress:${reuploadRow.id}`,
              collection: 'progress',
              op: 'upsert',
              payload: reuploadRow,
            });
            applyingRemote = true;
            try {
              await ob.drain(cl);
            } finally {
              applyingRemote = false;
            }
          })(),
        );
      }
    }
  }

  function subscribeToTree(): void {
    if (!client || !familyId) return;
    const filter = `family = "${familyId}"`;
    for (const collection of ['profiles', 'routines', 'tasks', 'profile_routines'] as const) {
      const unsub = client.subscribe(collection, filter, (event) => {
        console.info('[sync:engine] remote', {
          collection,
          action: event.action,
          id: event.record.id,
        });
        void track(handleTreeEvent(collection, event));
      });
      treeUnsubs.push(unsub);
    }
  }

  function subscribeToProgress(dateKey: string): void {
    if (!client || !familyId) return;
    if (progressUnsub) progressUnsub();
    progressUnsub = client.subscribe('progress', progressFilter(familyId, dateKey), (event) => {
      void track(handleProgressEvent(event));
    });
  }

  async function catchUp(): Promise<void> {
    if (!client || !familyId) return;
    const filter = `family = "${familyId}"`;
    const [profileRows, routineRows, taskRows, junctionRows] = await Promise.all([
      client.getFullList<ProfileRow>('profiles', filter),
      client.getFullList<RoutineRow>('routines', filter),
      client.getFullList<TaskRow>('tasks', filter),
      client.getFullList<ProfileRoutineRow>('profile_routines', filter),
    ]);
    const local = useSyncedStore.getState().tree;
    if (!local) return;
    let merged = local;
    for (const row of profileRows) merged = applyRemoteRecordToTree(merged, 'profiles', row);
    for (const row of routineRows) merged = applyRemoteRecordToTree(merged, 'routines', row);
    for (const row of junctionRows)
      merged = applyRemoteRecordToTree(merged, 'profile_routines', row);
    for (const row of taskRows) merged = applyRemoteRecordToTree(merged, 'tasks', row);
    // Buffered events may now be applicable — routines may have been
    // brought in by this catchUp.
    merged = drainAllPending(merged);
    if (merged !== local) applyRemote(() => useSyncedStore.getState().setTree(merged));

    // Push local-only tree items to PB. See the pre-v3 comment for
    // context — the same reasoning applies to the four collections.
    const ob = outbox;
    const cl = client;
    if (ob && cl) {
      const serverIds = {
        profiles: new Set(profileRows.map((r) => r.id)),
        routines: new Set(routineRows.map((r) => r.id)),
        tasks: new Set(taskRows.map((r) => r.id)),
        profile_routines: new Set(junctionRows.map((r) => r.id)),
      };
      const localFlat = flatten(merged, familyId);
      let enqueued = 0;
      for (const [id, row] of localFlat.profiles) {
        if (!serverIds.profiles.has(id)) {
          await ob.enqueue({
            id: `profiles:${id}`,
            collection: 'profiles',
            op: 'upsert',
            payload: row,
          });
          enqueued++;
        }
      }
      for (const [id, row] of localFlat.routines) {
        if (!serverIds.routines.has(id)) {
          await ob.enqueue({
            id: `routines:${id}`,
            collection: 'routines',
            op: 'upsert',
            payload: row,
          });
          enqueued++;
        }
      }
      for (const [id, row] of localFlat.tasks) {
        if (!serverIds.tasks.has(id)) {
          await ob.enqueue({
            id: `tasks:${id}`,
            collection: 'tasks',
            op: 'upsert',
            payload: row,
          });
          enqueued++;
        }
      }
      for (const [id, row] of localFlat.profileRoutines) {
        if (!serverIds.profile_routines.has(id)) {
          await ob.enqueue({
            id: `profile_routines:${id}`,
            collection: 'profile_routines',
            op: 'upsert',
            payload: row,
          });
          enqueued++;
        }
      }
      if (enqueued > 0) {
        applyingRemote = true;
        try {
          await ob.drain(cl);
        } finally {
          applyingRemote = false;
        }
      }
    }

    // Catch up today's progress. Unchanged from v2.
    const today = todayDateKey();
    const allProgressRows = await client.getFullList<ProgressRow>(
      'progress',
      progressFilter(familyId, today),
    );
    const byProfile = new Map<string, ProgressRow[]>();
    for (const row of allProgressRows) {
      const existing = byProfile.get(row.profile);
      if (existing) existing.push(row);
      else byProfile.set(row.profile, [row]);
    }
    for (const [pid, rows] of byProfile) {
      const remoteDaily = applyRemoteToProgress(rows);
      if (!remoteDaily) continue;
      const localDaily = await loadDailyProgress(pid, today);
      const mergedDaily = mergeProgress(localDaily, remoteDaily);
      await saveDailyProgress(mergedDaily);
      emitProgress(mergedDaily);
    }

    if (ob && cl && familyId) {
      const localTree = useSyncedStore.getState().tree;
      if (localTree) {
        const remoteByProfileRoutine = new Map<string, boolean[]>();
        for (const row of allProgressRows) {
          remoteByProfileRoutine.set(`${row.profile}_${row.routine}`, row.completed);
        }
        let progressEnqueued = 0;
        const libraryById = new Map(localTree.routines.map((r) => [r.id, r]));
        for (const prof of localTree.profiles) {
          const localDaily = await loadDailyProgress(prof.id, today);
          if (!localDaily) continue;
          for (const routineId of prof.routineIds) {
            const rout = libraryById.get(routineId);
            if (!rout) continue;
            const localArr = localDaily.routines[rout.id];
            if (!localArr?.some(Boolean)) continue;
            const remoteArr = remoteByProfileRoutine.get(`${prof.id}_${rout.id}`) ?? [];
            const hasUnpushed = localArr.some((v, i) => v && !(remoteArr[i] ?? false));
            if (!hasUnpushed) continue;
            const row = progressToRemote(localDaily, familyId, rout.id);
            await ob.enqueue({
              id: `progress:${row.id}`,
              collection: 'progress',
              op: 'upsert',
              payload: row,
            });
            progressEnqueued++;
          }
        }
        if (progressEnqueued > 0) {
          applyingRemote = true;
          try {
            await ob.drain(cl);
          } finally {
            applyingRemote = false;
          }
        }
      }
    }

    console.info('[sync:engine] catchUp', {
      profiles: profileRows.length,
      routines: routineRows.length,
      tasks: taskRows.length,
      profileRoutines: junctionRows.length,
      progressRows: allProgressRows.length,
    });
  }

  const engine: SyncEngine = {
    status() {
      return status;
    },
    familyId() {
      return familyId;
    },
    async start(opts) {
      if (status === 'running' || status === 'starting') return;
      status = 'starting';
      try {
        client = await getSyncClient();
        const auth = await client.authWithSyncCode(opts.syncCode);
        familyId = auth.familyId;
        console.info('[sync:engine] authed', { familyId });

        outbox = await createOutbox();

        subscribeToTree();
        subscribeToProgress(todayDateKey());
        if (client.onReconnect) {
          reconnectUnsub = client.onReconnect(() => {
            void engine.recover();
          });
        }
        console.info('[sync:engine] subscribed');

        await catchUp();
        await outbox.drain(client);

        status = 'running';

        setTreeChangeListener((prev, next) => {
          void engine.notifyTreeChange(prev, next);
        });
      } catch (e) {
        await stopInternal().catch(() => undefined);
        status = e instanceof SyncAuthError ? 'auth-failed' : 'stopped';
        throw e;
      }
    },
    async stop() {
      await stopInternal();
    },
    notifyTreeChange(prev, next) {
      if (status !== 'running') return Promise.resolve();
      if (applyingRemote) return Promise.resolve();
      const cl = client;
      const fid = familyId;
      const ob = outbox;
      if (!cl || !fid || !ob) return Promise.resolve();
      return track(
        (async () => {
          const ops = diffTree(prev, next, fid);
          for (const op of ops.upserts) {
            await ob.enqueue({
              id: `${op.collection}:${op.record.id}`,
              collection: op.collection,
              op: 'upsert',
              payload: op.record,
            });
          }
          for (const op of ops.deletes) {
            await ob.enqueue({
              id: `${op.collection}:${op.id}`,
              collection: op.collection,
              op: 'delete',
              payload: { id: op.id },
            });
          }
          const result = await ob.drain(cl);
          if (result.kind === 'auth-failed') {
            useDeviceStore.getState().setSyncAuthLost(true);
            await stopInternal();
          }
        })(),
      );
    },
    notifyProgress(daily, routineId) {
      if (status !== 'running') return Promise.resolve();
      if (applyingRemote) return Promise.resolve();
      const cl = client;
      const fid = familyId;
      const ob = outbox;
      if (!cl || !fid || !ob) return Promise.resolve();
      return track(
        (async () => {
          const row = progressToRemote(daily, fid, routineId);
          await ob.enqueue({
            id: `progress:${row.id}`,
            collection: 'progress',
            op: 'upsert',
            payload: row,
          });
          const result = await ob.drain(cl);
          if (result.kind === 'auth-failed') {
            useDeviceStore.getState().setSyncAuthLost(true);
            await stopInternal();
          }
        })(),
      );
    },
    onActiveProfileChanged() {
      // No-op — progress sub is family-wide.
    },
    onDayChanged(key) {
      if (status !== 'running') return;
      subscribeToProgress(key);
    },
    recover(): Promise<RecoverResult> {
      if (status !== 'running') return Promise.resolve({ kind: 'not-running' });
      const cl = client;
      const ob = outbox;
      if (!cl || !ob) return Promise.resolve({ kind: 'not-running' });
      return track(
        (async (): Promise<RecoverResult> => {
          try {
            await catchUp();
            const result = await ob.drain(cl);
            if (result.kind === 'auth-failed') {
              useDeviceStore.getState().setSyncAuthLost(true);
              await stopInternal();
            }
          } catch (e) {
            console.warn('[sync:engine] recover failed', e);
            throw e;
          }
          return { kind: 'recovered' };
        })(),
      );
    },
    async __resetForTests() {
      await stopInternal();
      pending.clear();
    },
    async __awaitIdle() {
      while (pending.size > 0) {
        await Promise.allSettled(Array.from(pending));
      }
    },
  };

  return engine;
}

export const syncEngine: SyncEngine = createSyncEngine();
